import { ChevronDown, LogOut, MessageCircle, Settings, Shield, ShieldAlert, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth, useUpdatePreferences } from "@/hooks/use-auth";
import { useBreakGlass } from "@/hooks/use-break-glass";
import { cn } from "@/lib/utils";
import { getUserDisplayName, getUserInitials, getUserRoleLabel } from "./user-display";
import { isChatWidgetEnabled } from "@/components/chat-widget/chat-preferences";
import { PersonAvatar } from "@/components/shared/person-avatar";

interface UserMenuProps {
  /** Use on dark gradient headers (account shell). */
  theme?: "default" | "on-dark";
  className?: string;
}

/** Matches App.tsx settings route guard - org admin settings only. */
const SETTINGS_ROLES = new Set(["super_admin", "org_admin"]);

export function UserMenu({ theme = "default", className }: UserMenuProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const displayName = getUserDisplayName(user);
  const roleLabel = getUserRoleLabel(user?.role);
  const initials = getUserInitials(user);
  const onDark = theme === "on-dark";
  const updatePreferences = useUpdatePreferences();
  const chatEnabled = isChatWidgetEnabled(user);
  const breakGlass = useBreakGlass();
  const canAccessSettings = SETTINGS_ROLES.has(user?.role ?? "");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "flex h-11 items-center gap-3 rounded-2xl border border-border/60 bg-background/80 px-3 outline-none shadow-sm transition-colors sm:gap-3.5 sm:pl-4 sm:pr-2",
          onDark ? "border-white/15 hover:bg-white/10" : "hover:border-primary/20 hover:bg-muted/40",
          className,
        )}
      >
        <PersonAvatar src={user?.avatar_url} initials={initials} size="md" tone={onDark ? "light" : "coral"} />
        <div className="hidden min-w-0 flex-col items-start sm:flex">
          <span
            className={cn(
              "max-w-[180px] truncate text-[15px] font-semibold leading-tight",
              onDark ? "text-white" : "text-foreground",
            )}
          >
            {displayName}
          </span>
          <span
            className={cn(
              "max-w-[180px] truncate text-xs leading-tight",
              onDark ? "text-white/70" : "text-muted-foreground",
            )}
          >
            {roleLabel}
          </span>
        </div>
        <ChevronDown className={cn("hidden h-4 w-4 sm:block", onDark ? "text-white/70" : "text-muted-foreground")} />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-72 rounded-xl p-0 shadow-lg border border-border">
        <div className="border-b border-border px-4 py-3">
          <div className="flex items-center gap-3">
            <PersonAvatar src={user?.avatar_url} initials={initials} size="md" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
              <p className="text-xs text-muted-foreground truncate">{roleLabel}</p>
              {user?.email && (
                <p className="text-xs text-muted-foreground/80 truncate mt-0.5">{user.email}</p>
              )}
            </div>
          </div>
        </div>

        <div className="p-1.5">
          <DropdownMenuItem
            onClick={() => navigate("/account/profile")}
            className="rounded-lg h-9 text-sm font-medium cursor-pointer"
          >
            <User className="mr-2.5 h-4 w-4 text-muted-foreground" />
            Profile
          </DropdownMenuItem>
          {canAccessSettings && (
            <>
              <DropdownMenuItem
                onClick={() => navigate("/settings")}
                className="rounded-lg h-9 text-sm font-medium cursor-pointer"
              >
                <Settings className="mr-2.5 h-4 w-4 text-muted-foreground" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate("/settings/security")}
                className="rounded-lg h-9 text-sm font-medium cursor-pointer"
              >
                <Shield className="mr-2.5 h-4 w-4 text-muted-foreground" />
                Security
              </DropdownMenuItem>
            </>
          )}
          {breakGlass.eligible && (
            <DropdownMenuItem
              onClick={() => {
                if (breakGlass.isActive) {
                  breakGlass.deactivate();
                } else {
                  breakGlass.openActivationModal();
                }
              }}
              className="rounded-lg h-9 text-sm font-medium cursor-pointer"
            >
              <ShieldAlert className="mr-2.5 h-4 w-4 text-amber-600" />
              <span className="flex-1">
                {breakGlass.isActive ? "End emergency access" : "Emergency access"}
              </span>
              {breakGlass.isActive && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
                  ON
                </span>
              )}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() =>
              updatePreferences.mutate({
                chat: { widgetEnabled: !chatEnabled },
              })
            }
            className="rounded-lg h-9 text-sm font-medium cursor-pointer"
          >
            <MessageCircle className="mr-2.5 h-4 w-4 text-muted-foreground" />
            <span className="flex-1">Show chat widget</span>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-bold",
                chatEnabled
                  ? "bg-primary/10 text-primary"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {chatEnabled ? "ON" : "OFF"}
            </span>
          </DropdownMenuItem>
        </div>

        <DropdownMenuSeparator className="my-0" />
        <div className="p-1.5">
          <DropdownMenuItem
            onClick={() => logout()}
            className="rounded-lg h-9 text-sm font-medium cursor-pointer text-destructive focus:bg-destructive/10 focus:text-destructive"
          >
            <LogOut className="mr-2.5 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
